import React, { useEffect, useRef, useState } from 'react';

import {
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
  makeStyles,
  Theme,
  createStyles,
} from '@material-ui/core';
import { HiOutlinePencil } from 'react-icons/hi';
import { IoArrowUndoOutline, IoCheckmark } from 'react-icons/io5';
import { MdTranslate } from 'react-icons/md';
import { RiReplyLine } from 'react-icons/ri';

import { blobToBase64 } from '../../utils/blobToBase64';
import { getFromLS, saveToLS } from '../../utils/local-storage';
import { AUTO_TRANSLATION_TARGET, TRANSLATION_LANGUAGES } from '../../constants/translation';
import { ChatMessage } from '../../types';
import { replyPreview } from './ChatMessage';
import { ChatComposer, ChatComposerBar, ChatComposerFiles, ChatComposerRef } from './kit';

interface Props {
  placeholder?: string;
  loading: boolean;
  // File types the attach button offers, as an <input accept> string.
  accept?: string;
  documents?: File[];
  images?: File[];
  // Picked or dropped files; the caller sorts pictures from documents and checks sizes.
  onAttach?: (files: File[]) => void;
  deleteDocument: (fileName: string) => void;
  deleteImage?: (image: string) => void;
  clearImages?: () => void;
  clearDocuments?: () => void;
  sendMessage: (message: string) => void;
  speechToText: (audio: string) => Promise<string | undefined>;
  messageToEdit?: ChatMessage | null;
  editMessage?: (messageId: string, newText: string) => Promise<void>;
  cancelEdit?: () => void;
  messageToReply?: ChatMessage | null;
  clearMessageToReply?: () => void;
  // Enables translating the draft before sending when provided
  translateDraft?: (text: string, target: string) => Promise<string | null>;
  // Remembers the last picked translation language per chat
  translationStorageKey?: string;
}

// The messaging composer: the shared chat composer plus reply, edit and
// draft translation.
export const ChatInput: React.FC<Props> = ({
  placeholder = 'Message',
  loading,
  accept,
  documents = [],
  images = [],
  onAttach,
  deleteDocument,
  deleteImage,
  clearImages,
  clearDocuments,
  sendMessage,
  speechToText,
  messageToEdit,
  editMessage,
  cancelEdit,
  messageToReply,
  clearMessageToReply,
  translateDraft,
  translationStorageKey,
}) => {
  const classes = useStyles();
  const composer = useRef<ChatComposerRef>(null);
  const [text, setText] = useState('');
  const [translating, setTranslating] = useState(false);
  const [translateMenuAnchor, setTranslateMenuAnchor] = useState<HTMLElement | null>(null);
  const [translationTarget, setTranslationTarget] = useState<string>(AUTO_TRANSLATION_TARGET);
  // Draft as it was before the last translation, for "Undo"
  const [draftBeforeTranslation, setDraftBeforeTranslation] = useState<string | null>(null);
  // What was typed before an edit started, put back if the edit is dropped
  const draftBeforeEdit = useRef('');

  const translationLSKey = translationStorageKey ? `chatTranslationTarget:${translationStorageKey}` : null;
  const hasAttachments = !!documents.length || !!images.length;
  const editing = !!messageToEdit?.id;

  useEffect(() => {
    setTranslationTarget((translationLSKey && getFromLS(translationLSKey)) || AUTO_TRANSLATION_TARGET);
    setDraftBeforeTranslation(null);
  }, [translationLSKey]);

  useEffect(() => {
    if (messageToEdit?.text) {
      draftBeforeEdit.current = text;
      setText(messageToEdit.text);
    }
  }, [messageToEdit]);

  useEffect(() => {
    if (messageToReply) {
      setTimeout(() => composer.current?.focus());
    }
  }, [messageToReply]);

  const onChange = (value: string) => {
    setText(value);
    // Hand edits make the pre-translation draft stale
    setDraftBeforeTranslation(null);
  };

  const dropEdit = () => {
    cancelEdit?.();
    setText(draftBeforeEdit.current);
    draftBeforeEdit.current = '';
  };

  const onSend = (value: string) => {
    if (translating) {
      return;
    }
    if (editing && editMessage) {
      if (value.trim() && value !== messageToEdit?.text) {
        editMessage(messageToEdit!.id!, value);
      } else {
        cancelEdit?.();
      }
      setText(draftBeforeEdit.current);
      draftBeforeEdit.current = '';
      return;
    }
    sendMessage(value);
    clearImages?.();
    clearDocuments?.();
    setText('');
    setDraftBeforeTranslation(null);
  };

  const handleTranslateDraft = async (target: string) => {
    setTranslateMenuAnchor(null);
    setTranslationTarget(target);
    if (translationLSKey) {
      saveToLS(translationLSKey, target);
    }
    if (!translateDraft || !text.trim()) {
      return;
    }
    const draft = text;
    setTranslating(true);
    const translation = await translateDraft(draft, target);
    setTranslating(false);
    if (translation) {
      // Re-translating keeps the original draft for "Undo"
      setDraftBeforeTranslation(prev => prev ?? draft);
      setText(translation);
    }
  };

  const undoTranslation = () => {
    if (draftBeforeTranslation !== null) {
      setText(draftBeforeTranslation);
      setDraftBeforeTranslation(null);
    }
  };

  const top = (
    <>
      {editing ? (
        <ChatComposerBar
          icon={<HiOutlinePencil size={18} />}
          title="Edit message"
          text={messageToEdit?.text}
          onClose={dropEdit}
          closeLabel="Cancel editing"
        />
      ) : (
        messageToReply && (
          <ChatComposerBar
            icon={<RiReplyLine size={18} />}
            title={`Reply to ${messageToReply.senderName}`}
            text={replyPreview(messageToReply)}
            onClose={() => clearMessageToReply?.()}
            closeLabel="Cancel reply"
          />
        )
      )}
      {draftBeforeTranslation !== null && (
        <ChatComposerBar
          icon={<MdTranslate size={18} />}
          title={
            translationTarget === AUTO_TRANSLATION_TARGET
              ? "Translated to the worker's language"
              : `Translated to ${translationTarget}`
          }
          text={draftBeforeTranslation}
          onClose={undoTranslation}
          closeLabel="Undo translation"
          closeIcon={<IoArrowUndoOutline size={18} />}
        />
      )}
      <ChatComposerFiles
        files={[
          ...images.map(image => ({
            key: `image-${image.name}`,
            name: image.name,
            size: image.size,
            image,
            onRemove: () => deleteImage?.(image.name),
          })),
          ...documents.map(file => ({
            key: `file-${file.name}`,
            name: file.name,
            size: file.size,
            onRemove: () => deleteDocument(file.name),
          })),
        ]}
      />
    </>
  );

  const translateButton = translateDraft && (!!text.trim() || translating) && (
    <>
      <Tooltip title="Translate before sending">
        <span>
          <IconButton
            className={classes.translate}
            disabled={translating || loading}
            onClick={event => setTranslateMenuAnchor(event.currentTarget)}
            aria-label="Translate message"
          >
            {translating ? <CircularProgress size={20} /> : <MdTranslate size={20} />}
          </IconButton>
        </span>
      </Tooltip>
      <Menu
        anchorEl={translateMenuAnchor}
        open={!!translateMenuAnchor}
        onClose={() => setTranslateMenuAnchor(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        getContentAnchorEl={null}
        PaperProps={{ style: { maxHeight: 360 } }}
      >
        {[AUTO_TRANSLATION_TARGET, ...TRANSLATION_LANGUAGES].map(target => (
          <MenuItem key={target} onClick={() => handleTranslateDraft(target)} className={classes.language}>
            {target === AUTO_TRANSLATION_TARGET ? "Auto — worker's language" : target}
            {target === translationTarget && <IoCheckmark size={18} className={classes.picked} />}
          </MenuItem>
        ))}
      </Menu>
    </>
  );

  return (
    <ChatComposer
      ref={composer}
      value={text}
      onChange={onChange}
      onSend={onSend}
      canSendEmpty={hasAttachments && !editing}
      editing={editing}
      sending={loading}
      disabled={translating}
      placeholder={placeholder}
      onCancel={editing ? dropEdit : messageToReply ? clearMessageToReply : undefined}
      onAttach={editing ? undefined : onAttach}
      accept={accept}
      top={top}
      extraActions={translateButton || undefined}
      onTranscribe={async blob => speechToText(await blobToBase64(blob))}
    />
  );
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    translate: { width: 40, height: 40, padding: 0, color: theme.palette.text.secondary },
    language: { display: 'flex', justifyContent: 'space-between', gap: 16 },
    picked: { color: theme.palette.primary.main },
  })
);
